import { cn } from "@/utils";
import type { ReactNode } from "react";
import { Text } from "./Text";

/**
 * Uma série de acontecimentos em ordem, um abaixo do outro: o horário à
 * esquerda do texto, ligados por um traço vertical. Serve para relatório de
 * incidente, histórico de um pedido, etapas de uma cobrança.
 *
 * É vertical em qualquer largura de propósito. Em grade, o olho lê na
 * horizontal e depois desce, e a ordem do tempo se perde. Numa coluna só, a
 * sequência é a própria leitura.
 *
 * O tom do marcador diz como aquele momento terminou (`danger` para a queda,
 * `success` para a volta). O texto diz a mesma coisa, porque cor não pode ser
 * o único meio de passar informação (WCAG 1.4.1).
 */

type TimelineTone = "default" | "danger" | "success";

export interface TimelineItem {
  /** Quando aconteceu, como o leitor fala: "10h40", "10h56 a 11h13", "16 out". */
  time: string;
  /** Data e hora em ISO para a tag `<time>`, quando houver uma exata. */
  dateTime?: string;
  children: ReactNode;
  tone?: TimelineTone;
}

export interface TimelineProps {
  items: TimelineItem[];
  /** Nome acessível da lista ("Linha do tempo do incidente"). */
  label: string;
  className?: string;
}

const dotClass: Record<TimelineTone, string> = {
  default: "border-ds-brand-700 bg-ds-surface",
  danger: "border-ds-danger-700 bg-ds-danger-700",
  success: "border-ds-success-700 bg-ds-success-700",
};

const timeTone: Record<TimelineTone, "brand" | "danger" | "success"> = {
  default: "brand",
  danger: "danger",
  success: "success",
};

export const Timeline = ({ items, label, className }: TimelineProps) => (
  <ol aria-label={label} className={cn("flex flex-col", className)}>
    {items.map(({ time, dateTime, children, tone = "default" }, index) => {
      const isLast = index === items.length - 1;
      return (
        <li key={`${time}-${index}`} className="flex gap-4">
          {/* Marcador e traço: decorativos, a ordem já está na lista. */}
          <div aria-hidden="true" className="flex flex-col items-center">
            <span
              className={cn(
                "mt-1.5 size-3 shrink-0 rounded-full border-2",
                dotClass[tone]
              )}
            />
            {isLast ? null : <span className="bg-ds-border w-px flex-1" />}
          </div>

          <div className={cn("flex min-w-0 flex-col gap-1", !isLast && "pb-8")}>
            <Text
              as={dateTime ? "time" : "p"}
              {...(dateTime ? { dateTime } : {})}
              size="small"
              weight="semibold"
              tone={timeTone[tone]}
            >
              {time}
            </Text>
            <Text as="div" tone="muted">
              {children}
            </Text>
          </div>
        </li>
      );
    })}
  </ol>
);
